import { Route, Router, Routes, hashIntegration, useParams } from "@solidjs/router";
import { Component, createSignal, onMount } from "solid-js";
import { JetMenu } from "./components/jetmenu/jetmenu";
import { LogoSlider } from "./components/logo-slider";
import { Header } from "./components/header";
import { Footer } from "./components/footer";
import toast, { Toaster } from "solid-toast";
import { HomeIndexPage as IntroductionPage } from "./pages/home";
import { HomeAboutUsPage as AboutUsPage } from "./pages/home/about-us";
import { createStore } from "solid-js/store";
import { cleanImagePath, fetchGet, getElementsFromHtml } from "./library/common";
import { SideBar } from "./components/side-bar";
import { HomeArtistListingPage as ArtistListingPage } from "./pages/home/artist-listing";
import { HomeArtistPage as ArtistPage } from "./pages/home/artist";
import { HomeSearchPage as SearchPage } from "./pages/home/search";
import { CMSBlock, CMSProvider } from "./components/cms/provider";
import { CategoryPage } from "./pages/category";
import { ArtworkPage } from "./pages/artwork/artwork";
import { AwardPage } from "./pages/artwork/award";
import { plainText } from "./components/cms/cms";
import { OtherAwardsPage } from "./pages/artwork/other-awards";
import { Ad } from "./Types";
import _ from "underscore";
import { ChairmansMessagePage } from "./pages/home/chairmans-message";
import { JurorsPage } from "./pages/home/jurors";

export const [ads, setAds] = createSignal<Ad[]>([]);
export const loadAds = async (section: string) => {
    const results = await getOrSetCache("ads/section/" + section, async () => {
        const res = await fetchGet("/Salon2010/Home/GetAdsJson", { section });
        const json = await res.json();
        return json.Data;
    });
    setAds(results);
};
export type Menu = ""|"Contents"|"Main Awards"|"Category Awards";
export type SubMenu = ""|"Introduction"|"Jurors"|"Artist Index"|"About Us"|"Search"|"Chairman's Message"|
"Best in Show"|"Other Awards"|"Bouguereau Award"|
"Figurative"|"Imaginative Realism"|"Animal"|"Landscape"|"Still Life"|"Sculpture"|"Drawing";
const category = {
    figurative: 10,
    animals: 9,
    imaginativeRealism: 8,
    stillLife: 7,
    landscape: 12,
    sculpture: 6,
    drawing: 11
};
export const [title, setTitle] = createSignal<string>("");
export const setPageTitle = ({ text, id }: { text?: string, id?: string; }) => {
    if (id) {
        if (!cms[id] || !cms[id].found) {
            setTitle(text || "");
        } else {
            setTitle(plainText(cms[id].item));
        }
        
    } else {
        setTitle(text || "");
    }
}
export const [menu, setMenu] = createSignal<Menu>("");
export const [submenu, setSubMenu] = createSignal<SubMenu>("Introduction");
export const [meta, setMeta] = createStore<any>();
export const [cms, setCms] = createStore<Record<string,CMSBlock>>({});
export const [cache, setCache] = createStore<Record<string,any>>({});
export const getOrSetCache = async (key: string, getter: () => Promise<any>) => {
    const item = cache[key];
    if (item != undefined) return item;
    const value = await getter();
    setCache(key, value);
    return value;
};
export const preloadImages = (urls: string[]) => {
    urls.forEach(url => {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = url;
        document.head.appendChild(link);
    });
}
export const loadCms = async (ids: string[]) => {
    const cachedIds = Object.keys(cms);
    const uncachedIds = ids.filter(id => !cachedIds.some(key => key == id));
    if (!uncachedIds.length) return;
    let json: any;
    try {
        const res = await fetchGet("/Home/GetContentBlocksJson", { ids: uncachedIds.join(",") });
        json = await res.json();
        if (!json.Success) return;
    } catch (ex) {
        console.log("loadCms", { ex });
        return;
    }
    let data: Record<string, CMSBlock> = {};
    uncachedIds.forEach(id => {
        let value = json.Data[id];
        if (!value || value.length === 0) {
            data[id] = { found: false, item: null };
            return;
        }
        data[id] = { found: true, item: value };
    });
    setCms(data);
}
export const loadPeoplesChoiceVotingCms = async () => {
    await loadCms([
        "7th-arc-salon/peoples-choice::page-title",
        "form::artist","form::first-name","form::last-name","form::title","form::medium",
        "form::category","form::award","7th-arc-salon/peoples-choice::form/just-show-my-nominations",
        "button::search","button::clear","7th-arc-salon/peoples-choice::works-nominated",
        "7th-arc-salon/peoples-choice::show-artists-starting-with"
    ]);
}
export const loadArtistCms = async () => {
    const ids = [
        "button::view-arc-gallery","7th-arc-salon/artist::biography-not-vetted"
    ];
    await loadCms(ids);
}
export const loadCategoriesCms = async () => {
    await loadCms([
        "7th-arc-salon/category::figurative",
        "7th-arc-salon/category::landscape",
        "7th-arc-salon/category::still-life",
        "7th-arc-salon/category::heading/winners",
        "7th-arc-salon/category::heading/honorable-mentions",
        "7th-arc-salon/category::heading/finalists"
    ]);
}
export const loadJurorsCms = async () => {
    const ids = [
        "7th-arc-salon/jurors::page-title", 
        "jurors::fred-ross",
        "jurors::michael-john-angel",
        "jurors::vern-swanson",
        "jurors::peter-trippi",
        "salon-application/jurors::jurors"
    ];
    await loadCms(ids);
}
export const loadChairmansMessageCms = async () =>{
    const ids = [
        "7th-arc-salon/chairmans-message::page-title",
        "7th-arc-salon/chairmans-message::page-body"
    ];
    await loadCms(ids);
}
export const loadAboutUsCms = async () => {
    const ids = [
        "7th-arc-salon/about-us::page-title",
        "7th-arc-salon/about-us::introduction","7th-arc-salon/about-us::heading/our-staff",
        "staff-listing::fred-ross","staff-listing::kara-ross:7th-arc-salon","staff-listing::iian-neill",
        "staff-listing::angela-swanson-jones","staff-listing::hongbin-zhang","staff-listing::frank-gatti",
        "staff-listing::yvette-lytle","staff-listing::sabrina-foland","staff-listing::michele-carnevale",
        "staff-listing::sherry-ross","staff-listing::sean-colon","staff-listing::thank-you"
    ];
    await loadCms(ids);
}
export const loadOtherAwardsCms = async () => {
    loadCms([
        "7th-arc-salon/other-awards::heading/other-awards",
        "7th-arc-salon/other-awards::title"
    ]);
}
export const loadSemiFinalistsCms = async () => {
    const ids = [
        "7th-arc-salon/artist-index::title",
        "7th-arc-salon/artist-index::page-body",
        "label::da-vinci-initiative"
    ];
    await loadCms(ids);
}
export const loadArtistIndexCms = async () => {
    const ids = [
        "7th-arc-salon/artist-index::title",
        "7th-arc-salon/artist-index::page-body",
        "label::da-vinci-initiative"
    ];
    await loadCms(ids);
}
export const App : Component = () => {
    const getCategory = async (id: number) => getOrSetCache("category/" + id, async () => {
        const res = await fetchGet("/Salon2010/Category/FindJson", { id });
        const json = await res.json();
        return json.Data;
    });
    const getCategoryWithWinners = async (id: number) => getOrSetCache("category-with-winners/" + id, async () => {
        const res = await fetchGet("/Salon2010/Category/GetCategoryWithWinnersJson", { id });
        const json = await res.json();
        return json.Data;
    });
    const getArtwork = async (id: number) => getOrSetCache("artwork/" + id, async () => {
        const res = await fetchGet("/Salon2010/Artwork/GetArtworkJson", { id });
        const json = await res.json();
        return json.Data;
    });
    const getArtworkByCategory = async (id: number) => getOrSetCache("artwork/by-category/" + id, async () => {
        const res = await fetchGet("/Salon2010/Artwork/GetArtworkByCategoryJson", { id });
        const json = await res.json();
        return json.Data;
    });
    const getArtworkByAward = async (award: string) => getOrSetCache("artwork/award/" + award, async () => {
        const res = await fetchGet("/Salon2010/Award/GetByAwardJson", { award });
        const json = await res.json();
        return json.Data;
    });
    const getArtistListingPageData = async () => getOrSetCache("artist-index/finalists", async () => {
        const res = await fetch("/Salon2010/Home/GetArtistListingJson");
        const json = await res.json();
        return json.Data;
    });
    const getOtherAwards = async () => getOrSetCache("other-awards", async () => {
        const res = await fetch("/Salon2010/Award/GetOtherAwardsJson");
        const json = await res.json();
        return json.Data;
    });
    const getEntrant = async (id: number) => getOrSetCache("entrant/" + id, async () => {
        const res = await fetchGet("/Salon2010/Home/GetEntrantJson", { id });
        const json = await res.json();
        return json.Data;
    })
    const loadArtist = async ({ navigate, params }: any) => {
        try {
            if (!params.artistId) {
                return navigate("/not-found");
            }
            const entrant = await getEntrant(params.artistId);
            return {
                entrant
            };
        } catch (ex: unknown) {
            toast.error("API Exception: " + (ex as Error).message);
            navigate("/error");
        }
    };
    const fetchCategoryWithWinners = async(categoryId: number, navigate: any) => {
        try {
            const model = await getCategoryWithWinners(categoryId);
            return { model };
        } catch (ex: unknown) {
            toast.error("API Exception: " + (ex as Error).message);
            navigate("/error");
        }
    }
    const loadAnimals = async ({ navigate}: any) => fetchCategoryWithWinners(category.animals, navigate);
    const loadFigurative = async ({ navigate}: any) => fetchCategoryWithWinners(category.figurative, navigate);
    const loadStillLife = async ({ navigate}: any) => fetchCategoryWithWinners(category.stillLife, navigate);
    const loadLandscape = async ({ navigate}: any) => fetchCategoryWithWinners(category.landscape, navigate);
    const loadDrawing = async ({ navigate}: any) => fetchCategoryWithWinners(category.drawing, navigate);
    const loadSculpture = async ({ navigate}: any) => fetchCategoryWithWinners(category.sculpture, navigate);
    const loadArtistListing = async ({ navigate }: any) => {
        try {
            const listing = await getArtistListingPageData();
            return { listing };
        } catch (ex: unknown) {
            toast.error("API Exception: " + (ex as Error).message);
            navigate("/error");
        }
    }
    const loadArtworkByCategory = async ({ params, navigate }:any) => {
        try {
            if (!params.artworkId) {
                return navigate("/not-found");
            }
            const model = await getArtworkByCategory(params.artworkId);
            return { model };
        } catch (ex: unknown) {
            toast.error("API Exception: " + (ex as Error).message);
            navigate("/error");
        }
    }
    const loadArtwork = async ({ params, navigate }:any) => {
        try {
            if (!params.artworkId) {
                return navigate("/not-found");
            }
            const model = await getArtwork(params.artworkId);
            return { model };
        } catch (ex: unknown) {
            toast.error("API Exception: " + (ex as Error).message);
            navigate("/error");
        }
    }
    const loadBestInShow = async ({ navigate}:any) => {
        try {
            const model = await getArtworkByAward("Best in Show");
            return { model, award: "Best in Show" };
        } catch (ex: unknown) {
            toast.error("API Exception: " + (ex as Error).message);
            navigate("/error");
        }
    }
    const loadBouguereauAward = async ({ navigate}:any) => {
        try {
            const model = await getArtworkByAward("Bouguereau Award");
            return { model, award: "Bouguereau Award" };
        } catch (ex: unknown) {
            toast.error("API Exception: " + (ex as Error).message);
            navigate("/error");
        }
    }
    const loadOtherAwards = async ({ navigate}:any) => {
        try {
            const grouping = await getOtherAwards();
            return { grouping }; 
        } catch (ex: unknown) {
            toast.error("API Exception: " + (ex as Error).message);
            navigate("/error");
        }
    }
    const preload = async () => {
        /**
         * /live-exhibition
         */
        await loadAds("7th-arc-salon/live-exhibition");
        /**
         * /artist-index
         */
        await loadArtistIndexCms();
        await loadAds("7th-arc-salon/introduction");
        await getArtistListingPageData();
        /**
         * Awards
         */
        await getArtworkByAward("Best in Show");
        await loadOtherAwardsCms();
        await getOtherAwards();
        /**
         * Categories
         */
        await loadAds("7th-arc-salon/figurative");
        await loadAds("7th-arc-salon/landscape");
        await loadAds("7th-arc-salon/sculpture");
        await loadAds("7th-arc-salon/drawing");
        await loadAds("7th-arc-salon/still-life");
        await loadCategoriesCms();
        await getCategoryWithWinners(category.figurative);
        /**
         * others
         */
        await loadJurorsCms();
        await loadAboutUsCms();
        /**
         * Images
         */
        const bestInShow = await getArtworkByAward("Best in Show");
        const bouguereauAward = await getArtworkByAward("William Bouguereau Award");
        preloadImages([
            cleanImagePath(bestInShow.Image.URL) + "?width=800&mode=fit",
            cleanImagePath(bouguereauAward.Image.URL) + "?width=800&mode=fit"
        ]);
        preloadImagesFromCms(["7th-arc-salon/sponsors::page-body"]);
        preloadImagesFromCms([
            "jurors::fred-ross","jurors::paul-mccormack","jurors::gabriel-weisberg","jurors::nelson-shanks"
        ]);
        preloadImagesFromCms([
            "staff-listing::fred-ross","staff-listing::kara-ross:7th-arc-salon","staff-listing::iian-neill",
            "staff-listing::angela-swanson-jones","staff-listing::hongbin-zhang","staff-listing::frank-gatti",
            "staff-listing::yvette-lytle","staff-listing::sabrina-foland","staff-listing::michele-carnevale",
            "staff-listing::sherry-ross","staff-listing::sean-colon"
        ]);
        const adImageUrls = ads().map(x=> x.Image.URL);
        preloadImages(adImageUrls);
    };
    const preloadImagesFromCms = (ids: string[]) => {
        const imgs = _.flatten(
            ids.map(id => getElementsFromHtml(cms[id]?.item, "img") as HTMLImageElement[])
        );
        const urls = imgs.map(x => x.src);
        preloadImages(urls);
    }
    onMount(async () => {
        await preload();
    })
    return (
    <Router source={hashIntegration()}>
        <div class="page-container">
            <CMSProvider cms={cms} setCms={setCms}>
                <Toaster />
                <JetMenu />
                <LogoSlider />
                <Header title={title}></Header>
                <section class="salon-results" style="background-color: #fff;">
                    <div id="main-template" style="display: table; width: 100%;">
                        <div style="display: table-row;">
                            <div class="left-cell">
                                <SideBar />
                            </div>
                            <div class="right-cell" style="margin: 0; padding: 0;">
                                <Routes>
                                    <Route path={"/artwork/by-category/:title/:artist/:artworkId"} component={ArtworkPage} data={loadArtworkByCategory}></Route>
                                    <Route path={"/artwork/:title/:artist/:artworkId"} component={ArtworkPage} data={loadArtwork}></Route>
                                    <Route path={"/artist/:artist/:artistId"} component={ArtistPage} data={loadArtist}></Route>
                                    <Route path={"/best-in-show"} component={AwardPage} data={loadBestInShow}></Route>
                                    <Route path={"/bouguereau-award"} component={AwardPage} data={loadBouguereauAward}></Route>
                                    <Route path={"/other-awards"} component={OtherAwardsPage} data={loadOtherAwards}></Route>
                                    <Route path={"/figurative"} component={CategoryPage} data={loadFigurative}></Route>
                                    <Route path={"/animals"} component={CategoryPage} data={loadAnimals}></Route>
                                    <Route path={"/landscape"} component={CategoryPage} data={loadLandscape}></Route>
                                    <Route path={"/drawing"} component={CategoryPage} data={loadDrawing}></Route>
                                    <Route path={"/sculpture"} component={CategoryPage} data={loadSculpture}></Route>
                                    <Route path={"/still-life"} component={CategoryPage} data={loadStillLife}></Route>
                                    <Route path={"/artist-index"} component={ArtistListingPage} data={loadArtistListing}></Route>
                                    <Route path={"/about-us"} component={AboutUsPage}></Route>
                                    <Route path={"/introduction"} component={IntroductionPage}></Route>
                                    <Route path={"/jurors"} component={JurorsPage}></Route>
                                    <Route path={"/chairmans-message"} component={ChairmansMessagePage}></Route>
                                    <Route path={"/search"} component={SearchPage}></Route>
                                    <Route path={"/"} component={IntroductionPage}></Route>
                                    <Route path={"/error"} component={() => {
                                        const params = useParams();
                                        return (
                                            <div>
                                                Sorry, there has been an error: {(params?.ex as any)?.Message}
                                            </div>
                                    )}}></Route>
                                </Routes> 
                            </div>
                        </div>
                    </div>
                </section>
                <Footer />
            </CMSProvider>
        </div>
    </Router>
    );
}

